import { resizeContainer, setLoading, showToast } from './form-helpers.js';
import { createFocusTrap } from './utils.js';
import { fetchJson, createIdempotencyKey } from '../../http/utils.js';
import { safeRedirect } from '../checkout-core/security.js';
import { logger } from './logger.js';

const LOGIN_ENDPOINT = '/api/method/cita_events.api.core.login.secure_login';

function isValidEmail(value) {
  const s = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && (window.citaBootData?.allowPlusAlias || !s.split('@')[0].includes('+'));
}

export function initLoginManagement(root, { triggerSoftReload } = {}) {
  const gateState = { busy: false };

  function openInlineLogin(form, resume) {
    const runExistingCleanup = () => {
      if (typeof form?.__inlineLoginCleanup === 'function') {
        try {
          form.__inlineLoginCleanup();
        } catch (error) {
          logger.error(error);
        }
      }
      if (form) {
        form.__inlineLoginCleanup = null;
      }
    };

    const clearInlineLoginActive = () => {
      if (form?.dataset.inlineLoginActive) {
        delete form.dataset.inlineLoginActive;
      }
    };

    runExistingCleanup();
    const panel = form?.querySelector('[data-login-inline]');
    if (!panel) {
      clearInlineLoginActive();
      if (typeof resume === 'function') {
        try {
          resume();
        } catch (error) {
          logger.error(error);
        }
      }
      return;
    }

    const emailInput = panel.querySelector('[data-login-email]');
    const passwordInput = panel.querySelector('[data-login-password]');
    const submitBtn = panel.querySelector('[data-login-inline-submit]');
    const guestBtn = panel.querySelector('[data-login-inline-guest]');
    const feedback = panel.querySelector('[data-login-inline-feedback]');
    const formEmail = form.querySelector('input[name="email"]');
    const submitLabelEl = submitBtn?.querySelector('span:not(.loader)') || submitBtn;
    const originalSubmitLabel = submitLabelEl ? submitLabelEl.textContent : '';
    const twoFactorState = { tmpId: '' };

    const setSubmitLabel = (isTwoFactor) => {
      if (!submitLabelEl) return;
      submitLabelEl.textContent = isTwoFactor ? __('Code prüfen') : (originalSubmitLabel || __('Anmelden'));
    };

    const resetTwoFactorMode = () => {
      twoFactorState.tmpId = '';
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.type = 'password';
        passwordInput.placeholder = __('Passwort');
        passwordInput.setAttribute('autocomplete', 'current-password');
        passwordInput.removeAttribute('inputmode');
      }
      resetToggleState();
      setSubmitLabel(false);
    };

    const activateTwoFactorMode = (payload) => {
      const tmpId = typeof payload?.data?.tmp_id === 'string' ? payload.data.tmp_id.trim() : '';
      if (!tmpId) return false;

      const verification = payload?.data?.verification;
      const prompt = typeof verification?.prompt === 'string' ? verification.prompt.trim() : '';

      twoFactorState.tmpId = tmpId;
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.type = 'text';
        passwordInput.placeholder = __('Verifizierungscode');
        passwordInput.setAttribute('autocomplete', 'one-time-code');
        passwordInput.setAttribute('inputmode', 'numeric');
      }
      setSubmitLabel(true);
      if (feedback) {
        feedback.textContent = prompt || __('Zwei-Faktor-Verifizierung erforderlich.');
        feedback.classList.remove('hidden');
      }
      setTimeout(() => passwordInput?.focus(), 0);
      return true;
    };

    // A11y: Store trigger element to restore focus on close
    const triggerElement = document.activeElement;

    if (emailInput && formEmail) {
      emailInput.value = (formEmail.value || '').trim();
    }
    resetTwoFactorMode();
    if (passwordInput) setTimeout(() => {
      passwordInput.focus();
      passwordInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
    if (feedback) {
      feedback.classList.add('hidden');
      feedback.textContent = '';
    }

    panel.classList.remove('hidden');
    requestAnimationFrame(() => resizeContainer(form));

    // Password toggle (generic, no IDs — works across all themes)
    const toggleBtn = panel.querySelector('[data-login-toggle-password]');
    let pwVisible = false;
    const EYE_OPEN = 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z';
    const EYE_CLOSED = 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21';
    const resetToggleState = () => {
      if (!toggleBtn) return;
      pwVisible = false;
      toggleBtn.setAttribute('aria-pressed', 'false');
      const iconPath = toggleBtn.querySelector('[data-login-toggle-icon-path]');
      if (iconPath) {
        iconPath.setAttribute('d', EYE_OPEN);
      }
    };
    const handleToggle = toggleBtn && passwordInput ? () => {
      pwVisible = !pwVisible;
      passwordInput.type = pwVisible ? 'text' : 'password';
      toggleBtn.setAttribute('aria-pressed', String(pwVisible));
      const iconPath = toggleBtn.querySelector('[data-login-toggle-icon-path]');
      if (iconPath) {
        iconPath.setAttribute('d', pwVisible ? EYE_CLOSED : EYE_OPEN);
      }
    } : null;
    if (toggleBtn && handleToggle) {
      toggleBtn.addEventListener('click', handleToggle);
    }

    let busy = false;

    const cleanup = () => {
      resetTwoFactorMode();
      panel.classList.add('hidden');
      requestAnimationFrame(() => resizeContainer(form));
      if (feedback) {
        feedback.classList.add('hidden');
        feedback.textContent = '';
      }
      if (passwordInput) {
        passwordInput.value = '';
      }
      if (toggleBtn && handleToggle) {
        toggleBtn.removeEventListener('click', handleToggle);
      }
      submitBtn?.removeEventListener('click', handleLogin);
      guestBtn?.removeEventListener('click', handleGuest);
      if (focusHandler) {
        document.removeEventListener('keydown', focusHandler);
      }
      clearInlineLoginActive();
      if (form && form.__inlineLoginCleanup === cleanup) {
        form.__inlineLoginCleanup = null;
      }
      // A11y: Restore focus to the element that triggered the login panel
      if (triggerElement && typeof triggerElement.focus === 'function') {
        try { triggerElement.focus(); } catch (_) { /* element may be gone */ }
      }
    };

    const handleLogin = async (event) => {
      event.preventDefault();
      if (busy) {
        return;
      }
      const email = (emailInput?.value || '').trim();
      const secret = passwordInput?.value || '';
      if (!secret) {
        if (feedback) {
          feedback.textContent = twoFactorState.tmpId
            ? __('Bitte Verifizierungscode eingeben.')
            : __('Bitte E-Mail und Passwort eingeben.');
          feedback.classList.remove('hidden');
        }
        return;
      }
      if (!twoFactorState.tmpId && !email) {
        if (feedback) {
          feedback.textContent = __('Bitte E-Mail und Passwort eingeben.');
          feedback.classList.remove('hidden');
        }
        return;
      }

      busy = true;
      setLoading(submitBtn, true);
      if (feedback) {
        feedback.classList.add('hidden');
      }

      try {
        const body = twoFactorState.tmpId
          ? { otp: secret, tmp_id: twoFactorState.tmpId }
          : { usr: email, pwd: secret };
        const { res, payload } = await fetchJson(LOGIN_ENDPOINT, {
          method: 'POST',
          body,
          headers: { 'X-Idempotency-Key': createIdempotencyKey() },
        });

        if (payload?.code === 'two_factor_required') {
          const activated = activateTwoFactorMode(payload);
          if (!activated) {
            resetTwoFactorMode();
            if (feedback) {
              feedback.textContent = __('Zwei-Faktor-Verifizierung konnte nicht gestartet werden.');
              feedback.classList.remove('hidden');
            }
            showToast(__('Zwei-Faktor-Verifizierung konnte nicht gestartet werden.'), 'error');
          }
          return;
        }

        if (payload?.code === 'password_reset_required') {
          const redirectTo = typeof payload?.data?.redirect_to === 'string'
            ? payload.data.redirect_to.trim()
            : '/update-password';
          safeRedirect(redirectTo, '/update-password');
          return;
        }

        if (!payload?.success) {
          if (feedback) {
            if (twoFactorState.tmpId && (payload?.code === 'login_failed' || payload?.code === 'unauthorized')) {
              feedback.textContent = __('Verifizierungscode ungültig oder abgelaufen.');
            } else {
              feedback.textContent = payload?.toast || __('Login fehlgeschlagen. Bitte prüfe E-Mail/Passwort.');
            }
            feedback.classList.remove('hidden');
          }
          // UX: Clear password/OTP and refocus for quick retry
          if (passwordInput) { passwordInput.value = ''; passwordInput.focus(); }
          throw new Error(payload?.toast || `HTTP ${res.status}`);
        }

        const redirectTo = typeof payload?.data?.redirect_to === 'string'
          ? payload.data.redirect_to.trim()
          : '';
        if (redirectTo) {
          safeRedirect(redirectTo, window.location.pathname);
          return;
        }

        showToast(__('Erfolgreich eingeloggt. Weiter geht\'s …'), 'success');
        if (typeof form.__inlineLoginCleanup === 'function') {
          try {
            form.__inlineLoginCleanup();
          } catch (error) {
            logger.error(error);
          }
        }
        form.__inlineLoginCleanup = null;
        if (typeof resume === 'function') {
          try {
            resume();
          } catch (error) {
            logger.error(error);
          }
        }
        if (typeof triggerSoftReload === 'function') {
          try {
            triggerSoftReload();
          } catch (error) {
            logger.error(error);
          }
        }
      } catch (error) {
        if (feedback && feedback.classList.contains('hidden')) {
          feedback.textContent = __('Login fehlgeschlagen. Bitte prüfe E-Mail/Passwort.');
          feedback.classList.remove('hidden');
        }
        showToast(__('Login fehlgeschlagen.'), 'error');
      } finally {
        busy = false;
        setLoading(submitBtn, false);
        setSubmitLabel(!!twoFactorState.tmpId);
      }
    };

    const handleGuest = (event) => {
      event.preventDefault();
      if (busy) {
        return;
      }
      if (typeof form.__inlineLoginCleanup === 'function') {
        try {
          form.__inlineLoginCleanup();
        } catch (error) {
          logger.error(error);
        }
      }
      form.__inlineLoginCleanup = null;
      if (typeof resume === 'function') {
        try {
          resume();
        } catch (error) {
          logger.error(error);
        }
      }
    };

    const focusHandler = createFocusTrap(panel, {
      onEscape: cleanup,
      submitInput: passwordInput,
      onSubmit: handleLogin,
    });

    document.addEventListener('keydown', focusHandler);
    submitBtn?.addEventListener('click', handleLogin);
    guestBtn?.addEventListener('click', handleGuest);
    form.__inlineLoginCleanup = cleanup;
  }

  function createGateHandler(form) {
    return async function gateGuestPaidSubmit(event) {
      if (!form.querySelector('[data-login-inline]')) {
        return;
      }

      if (form.dataset.inlineBypass === '1') {
        delete form.dataset.inlineBypass;
        return;
      }

      const haltEvent = () => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      };

      haltEvent();
      const submitBtn = form.querySelector('button[type=submit]');
      setLoading(submitBtn, true);

      const clearPendingGate = () => {
        if (form.dataset.inlineGatePending === '1') {
          delete form.dataset.inlineGatePending;
        }
      };

      form.dataset.inlineGatePending = '1';

      if (gateState.busy) {
        clearPendingGate();
        setLoading(submitBtn, false);
        return;
      }

      const emailInput = form.querySelector('input[name="email"]');
      const email = (emailInput?.value || '').trim();
      if (!isValidEmail(email)) {
        showToast(__('Bitte gib zuerst eine gültige E-Mail ein.'), 'warning');
        emailInput?.focus();
        clearPendingGate();
        setLoading(submitBtn, false);
        return;
      }

      // Enforce account check for 'required' and 'optional' policies.
      // Only skip for 'not_required' (no login prompt, always guest).
      const accountPolicy = form.dataset.accountPolicy
        || document.getElementById('event_account_policy')?.value
        || 'optional';
      if (accountPolicy === 'not_required') {
        clearPendingGate();
        setLoading(submitBtn, false);
        form.removeEventListener('submit', gateGuestPaidSubmit, true);
        // Defer to next task: requestSubmit() is a no-op while
        // "firing submission events" flag is set (HTML spec §4.10.21.3)
        setTimeout(() => form.requestSubmit(), 0);
        return;
      }

      gateState.busy = true;
      try {
        // TPA-094: account-existence pre-check removed — email_has_account is a
        // permanent no-op stub (always returns exists=false; CWE-200 fix). The
        // real account_check is server-driven: the registration endpoint returns
        // an account_check / account_exists error code which triggers the inline
        // login modal. Proceed directly to form submission.
        if (typeof form.__inlineLoginCleanup === 'function') {
          try {
            form.__inlineLoginCleanup();
          } catch (error) {
            logger.error(error);
          }
        }
        form.__inlineLoginCleanup = null;
        delete form.dataset.inlineLoginActive;
        clearPendingGate();
        setLoading(submitBtn, false);
        form.dataset.inlineBypass = '1';
        if (form.dataset.inlineBypass === '1') {
          form.requestSubmit();
        }
      } catch (error) {
        if (typeof form.__inlineLoginCleanup === 'function') {
          try {
            form.__inlineLoginCleanup();
          } catch (cleanupError) {
            logger.error(cleanupError);
          }
        }
        form.__inlineLoginCleanup = null;
        delete form.dataset.inlineLoginActive;
        clearPendingGate();
        setLoading(submitBtn, false);
        form.dataset.inlineBypass = '1';
        if (form.dataset.inlineBypass === '1') {
          form.requestSubmit();
        }
      } finally {
        clearPendingGate();
        gateState.busy = false;
      }
    };
  }

  function attachGuestGate(forms) {
    forms.forEach((form) => {
      if (!form || form.dataset.guestGate === '1') {
        return;
      }
      const handler = createGateHandler(form);
      form.addEventListener('submit', handler, { capture: true });
      form.dataset.guestGate = '1';
    });
  }

  return { openInlineLogin, attachGuestGate };
}
